const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function getAuthHeaders() {
  const token = localStorage.getItem('admin_token');
  if (token) return { Authorization: `Bearer ${token}` };
  return {};
}

async function fetchApi(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  if (!res.ok) {
    if (res.status === 401) {
      // Token expired or invalid
      const isAdminRoute = window.location.pathname.startsWith('/admin');
      if (isAdminRoute) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/admin/login';
      }
    }
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || err.message || 'API Fehler');
  }
  return res.json();
}

async function fetchApiAuth(path, options = {}) {
  return fetchApi(path, {
    ...options,
    headers: { ...getAuthHeaders(), ...options.headers },
  });
}

export const api = {
  // Auth
  login: (email, password) => fetchApi('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  getMe: () => fetchApiAuth('/auth/me'),
  register: (data) => fetchApiAuth('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  getUsers: () => fetchApiAuth('/auth/users'),
  toggleUser: (id) => fetchApiAuth(`/auth/users/${id}`, { method: 'PUT' }),
  deleteUser: (id) => fetchApiAuth(`/auth/users/${id}`, { method: 'DELETE' }),

  // Providers (public read, auth write)
  getProviders: (activeOnly = false) => fetchApi(`/providers${activeOnly ? '?active_only=true' : ''}`),
  getProvider: (id) => fetchApi(`/providers/${id}`),
  createProvider: (data) => fetchApiAuth('/providers', { method: 'POST', body: JSON.stringify(data) }),
  updateProvider: (id, data) => fetchApiAuth(`/providers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProvider: (id) => fetchApiAuth(`/providers/${id}`, { method: 'DELETE' }),

  // TLDs (public read, auth write)
  getTlds: (category, activeOnly = false) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (activeOnly) params.set('active_only', 'true');
    return fetchApi(`/tlds?${params}`);
  },
  getTld: (id) => fetchApi(`/tlds/${id}`),
  createTld: (data) => fetchApiAuth('/tlds', { method: 'POST', body: JSON.stringify(data) }),
  updateTld: (id, data) => fetchApiAuth(`/tlds/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTld: (id) => fetchApiAuth(`/tlds/${id}`, { method: 'DELETE' }),

  // Prices (public read, auth write)
  getPrices: (providerId, tld) => {
    const params = new URLSearchParams();
    if (providerId) params.set('provider_id', providerId);
    if (tld) params.set('tld', tld);
    return fetchApi(`/prices?${params}`);
  },
  createPrice: (data) => fetchApiAuth('/prices', { method: 'POST', body: JSON.stringify(data) }),
  updatePrice: (id, data) => fetchApiAuth(`/prices/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePrice: (id) => fetchApiAuth(`/prices/${id}`, { method: 'DELETE' }),

  // Promotions (public read, auth write)
  getPromotions: (providerId, tld, activeOnly = false) => {
    const params = new URLSearchParams();
    if (providerId) params.set('provider_id', providerId);
    if (tld) params.set('tld', tld);
    if (activeOnly) params.set('active_only', 'true');
    return fetchApi(`/promotions?${params}`);
  },
  createPromotion: (data) => fetchApiAuth('/promotions', { method: 'POST', body: JSON.stringify(data) }),
  updatePromotion: (id, data) => fetchApiAuth(`/promotions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePromotion: (id) => fetchApiAuth(`/promotions/${id}`, { method: 'DELETE' }),

  // Deals (public read, auth write)
  getDeals: (tld, providerId) => {
    const params = new URLSearchParams();
    if (tld) params.set('tld', tld);
    if (providerId) params.set('provider_id', providerId);
    return fetchApi(`/deals?${params}`);
  },
  createDeal: (data) => fetchApiAuth('/deals', { method: 'POST', body: JSON.stringify(data) }),
  updateDeal: (id, data) => fetchApiAuth(`/deals/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteDeal: (id) => fetchApiAuth(`/deals/${id}`, { method: 'DELETE' }),

  // Compare (public)
  compare: (tld, years = 1, priceType = 'registration', sortBy = 'total') =>
    fetchApi(`/compare?tld=${encodeURIComponent(tld)}&years=${years}&price_type=${priceType}&sort_by=${sortBy}`),
  compareMulti: (tlds, years = 1, priceType = 'registration') =>
    fetchApi(`/compare/multi?tlds=${encodeURIComponent(tlds.join(','))}&years=${years}&price_type=${priceType}`),

  // Search (public)
  search: (q = '') => fetchApi(`/search?q=${encodeURIComponent(q)}`),

  // Import (auth)
  importPreview: (data, format) => fetchApiAuth('/import/preview', { method: 'POST', body: JSON.stringify({ data, format }) }),
  importFetch: (data) => fetchApiAuth('/import/fetch', { method: 'POST', body: JSON.stringify(data) }),
  importApply: (data) => fetchApiAuth('/import/apply', { method: 'POST', body: JSON.stringify(data) }),

  // Import Sources (auth)
  getImportSources: (providerId) => fetchApiAuth(`/import-sources${providerId ? `?provider_id=${providerId}` : ''}`),
  saveImportSource: (data) => fetchApiAuth('/import-sources', { method: 'POST', body: JSON.stringify(data) }),

  // Price History (public)
  getPriceHistory: (providerId, tld, limit = 30) =>
    fetchApi(`/price-history?provider_id=${providerId}&tld=${encodeURIComponent(tld)}&limit=${limit}`),

  // Stats (public basic, auth for admin stats)
  getStats: () => fetchApi('/stats'),
  getAdminStats: () => fetchApiAuth('/admin/stats'),

  // Scheduler (auth)
  getSchedulerLogs: (limit = 50) => fetchApiAuth(`/scheduler/logs?limit=${limit}`),
  triggerScheduler: () => fetchApiAuth('/scheduler/run-now', { method: 'POST' }),

  // Provider-Accounts (admin)
  getProviderAccounts: () => fetchApiAuth('/admin/provider-accounts'),
  approveProviderAccount: (id) => fetchApiAuth(`/admin/provider-accounts/${id}/approve`, { method: 'POST' }),
  blockProviderAccount: (id) => fetchApiAuth(`/admin/provider-accounts/${id}/block`, { method: 'POST' }),
  setProviderTier: (id, tier) => fetchApiAuth(`/admin/provider-accounts/${id}/set-tier?tier=${tier}`, { method: 'POST' }),

  // Invoices (admin)
  getInvoices: (status) => fetchApiAuth(`/admin/invoices${status ? `?status=${status}` : ''}`),
  createInvoice: (data) => fetchApiAuth('/admin/invoices', { method: 'POST', body: JSON.stringify(data) }),
  markInvoicePaid: (id) => fetchApiAuth(`/admin/invoices/${id}/mark-paid`, { method: 'POST' }),
  runRecurring: () => fetchApiAuth('/admin/invoices/run-recurring', { method: 'POST' }),
  getInvoicePdf: async (id) => {
    // PDF-Route liefert HTML (nicht JSON) -> eigener Fetch mit res.text()
    const token = localStorage.getItem('admin_token');
    const res = await fetch(`/api/admin/invoices/${id}/pdf`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(err.detail || err.message || 'API Fehler');
    }
    return res.text();
  },

  // Seed
  seed: () => fetchApi('/seed', { method: 'POST' }),
};

// Auth helpers
export function isLoggedIn() {
  return !!localStorage.getItem('admin_token');
}

export function getUser() {
  const userStr = localStorage.getItem('admin_user');
  if (userStr) return JSON.parse(userStr);
  return null;
}

export function logout() {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_user');
}
